import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Users, Quote, ArrowLeft, ArrowRight, Play, Pause, ThumbsUp, MessageCircle } from 'lucide-react';

const testimonialsData = [
  {
    id: 1,
    name: 'Ana López',
    role: 'CEO en TechNova',
    company: 'TechNova Solutions',
    quote: 'Visioner7 transformó nuestra app obsoleta en una plataforma escalable que maneja 10,000 usuarios simultáneos. Su expertise en desarrollo móvil superó todas nuestras expectativas.',
    avatar: '/images/testimonials/ana-lopez.jpg',
    rating: 5,
    project: 'App de Gestión Empresarial',
    duration: '6 meses',
    results: ['+300% rendimiento', '99.9% uptime', '5★ rating en stores'],
    videoTestimonial: '/videos/testimonial-ana.mp4'
  },
  {
    id: 2,
    name: 'Carlos Ruiz',
    role: 'Gerente Financiero',
    company: 'FinCorp International',
    quote: 'Las soluciones contables personalizadas de Visioner7 nos ahorraron más de 40 horas semanales en procesos manuales. La automatización de reportes fiscales es simplemente brillante.',
    avatar: '/images/testimonials/carlos-ruiz.jpg',
    rating: 5,
    project: 'Sistema Contable Inteligente',
    duration: '4 meses',
    results: ['-40h/semana', '0 errores', 'Reportes en tiempo real'],
    featured: true
  },
  {
    id: 3,
    name: 'María González',
    role: 'Directora de TI',
    company: 'InnovateLab',
    quote: 'Las APIs desarrolladas por Visioner7 son increíblemente rápidas y seguras. Nuestro equipo de desarrollo puede ahora enfocarse en innovación en lugar de resolver problemas técnicos.',
    avatar: '/images/testimonials/maria-gonzalez.jpg',
    rating: 5,
    project: 'Plataforma API Empresarial',
    duration: '5 meses',
    results: ['-200ms latency', '100% seguridad', 'Escalabilidad infinita']
  },
  {
    id: 4,
    name: 'Juan Pérez',
    role: 'Fundador',
    company: 'StartupX',
    quote: 'Los sistemas personalizados crecen con nosotros. Visioner7 no solo desarrolló nuestra plataforma, sino que se convirtió en nuestro partner tecnológico estratégico.',
    avatar: '/images/testimonials/juan-perez.jpg',
    rating: 5,
    project: 'Ecosistema Startup',
    duration: '8 meses',
    results: ['+500% crecimiento', '3 nuevos productos', 'Expansión internacional'],
    featured: true
  },
  {
    id: 5,
    name: 'Laura Martínez',
    role: 'Directora de Operaciones',
    company: 'RetailPro',
    quote: 'La implementación del sistema ERP fue impecable. Visioner7 entendió perfectamente nuestras necesidades y entregó una solución que optimizó toda nuestra cadena de suministro.',
    avatar: '/images/testimonials/laura-martinez.jpg',
    rating: 5,
    project: 'Sistema ERP Retail',
    duration: '7 meses',
    results: ['-30% costos', '+25% eficiencia', 'Integración completa']
  },
  {
    id: 6,
    name: 'Roberto Silva',
    role: 'CTO',
    company: 'DataDrive',
    quote: 'El trabajo en machine learning y análisis de datos que realizó Visioner7 nos permitió descubrir insights que han sido cruciales para nuestra estrategia de negocio.',
    avatar: '/images/testimonials/roberto-silva.jpg',
    rating: 5,
    project: 'Plataforma de Analytics',
    duration: '6 meses',
    results: ['+15% conversión', 'Predictivos 95% precisos', 'Dashboard en tiempo real']
  }
];

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  // Testimonios destacados
  const featuredTestimonials = testimonialsData.filter(t => t.featured);
  const regularTestimonials = testimonialsData.filter(t => !t.featured);

  useEffect(() => {
    if (!isAutoPlay || isPlayingVideo) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % featuredTestimonials.length);
    }, 6000);
    
    return () => clearInterval(interval);
  }, [isAutoPlay, isPlayingVideo, featuredTestimonials.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + featuredTestimonials.length) % featuredTestimonials.length);
    setIsAutoPlay(false);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % featuredTestimonials.length);
    setIsAutoPlay(false);
  };

  const currentTestimonial = featuredTestimonials[currentIndex];

  const toggleVideo = (testimonialId) => {
    if (activeVideo === testimonialId) {
      setActiveVideo(null);
      setIsPlayingVideo(false);
    } else {
      setActiveVideo(testimonialId);
      setIsPlayingVideo(true);
      setIsAutoPlay(false);
    }
  };

  const StarRating = ({ rating }) => (
    <div className="flex justify-center gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-5 h-5 ${i < rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );

  return (
    <section id="testimonios" className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Encabezado */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 mb-4 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-white shadow-lg"
          >
            <Users className="w-5 h-5" />
            <span className="font-semibold">Testimonios de Clientes</span>
          </motion.div>
          
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Historias de <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Éxito</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Descubre cómo empresas líderes han transformado sus operaciones y alcanzado nuevos niveles 
            de eficiencia con nuestras soluciones tecnológicas.
          </p>
        </motion.div>

        {/* Testimonio Principal Destacado */}
        <motion.div
          className="relative max-w-6xl mx-auto mb-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-2xl overflow-hidden border border-blue-100">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Contenido del testimonio */}
              <div className="p-8 md:p-12">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentTestimonial.id}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.5 }}
                    className="h-full flex flex-col justify-center"
                  >
                    {/* Rating y Icono */}
                    <div className="flex items-center justify-between mb-6">
                      <StarRating rating={currentTestimonial.rating} />
                      <div className="text-blue-600">
                        <Quote className="w-8 h-8" />
                      </div>
                    </div>

                    {/* Testimonio */}
                    <blockquote className="text-2xl md:text-3xl font-medium text-gray-800 leading-relaxed mb-6">
                      "{currentTestimonial.quote}"
                    </blockquote>

                    {/* Información del cliente */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                        {currentTestimonial.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-gray-900">{currentTestimonial.name}</h4>
                        <p className="text-gray-600">{currentTestimonial.role}</p>
                        <p className="text-blue-600 font-semibold">{currentTestimonial.company}</p>
                      </div>
                    </div>

                    {/* Resultados del proyecto */}
                    <div className="bg-blue-50 rounded-2xl p-4">
                      <h5 className="font-semibold text-gray-900 mb-2">Resultados del Proyecto:</h5>
                      <div className="flex flex-wrap gap-2">
                        {currentTestimonial.results.map((result, index) => (
                          <span key={index} className="bg-white text-blue-600 px-3 py-1 rounded-full text-sm font-medium">
                            {result}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Panel derecho con información del proyecto */}
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-8 md:p-12 text-white relative">
                <div className="h-full flex flex-col justify-center">
                  <h3 className="text-2xl font-bold mb-4">Detalles del Proyecto</h3>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <span className="text-blue-200">Proyecto:</span>
                      <p className="font-semibold text-lg">{currentTestimonial.project}</p>
                    </div>
                    
                    <div>
                      <span className="text-blue-200">Duración:</span>
                      <p className="font-semibold">{currentTestimonial.duration}</p>
                    </div>
                    
                    <div>
                      <span className="text-blue-200">Entregables:</span>
                      <ul className="list-disc list-inside space-y-1 mt-1">
                        {currentTestimonial.results.map((result, index) => (
                          <li key={index}>{result}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Video testimonial (si está disponible) */}
                  {currentTestimonial.videoTestimonial && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => toggleVideo(currentTestimonial.id)}
                      className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-2xl px-4 py-3 hover:bg-white/30 transition-all"
                    >
                      <Play className="w-5 h-5" />
                      <span>Ver testimonio en video</span>
                    </motion.button>
                  )}

                  {/* Controles de navegación */}
                  <div className="flex items-center justify-between mt-8">
                    <div className="flex items-center gap-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setIsAutoPlay(!isAutoPlay)}
                        className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                      >
                        {isAutoPlay ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </motion.button>
                      <span className="text-sm text-blue-200">
                        {currentIndex + 1} / {featuredTestimonials.length}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={handlePrev}
                        className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                      >
                        <ArrowLeft className="w-5 h-5" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={handleNext}
                        className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                      >
                        <ArrowRight className="w-5 h-5" />
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Indicadores */}
          <div className="flex justify-center gap-2 mt-6">
            {featuredTestimonials.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300'
                }`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </div>
        </motion.div>

        {/* Grid de Testimonios Secundarios */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {regularTestimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 group"
              whileHover={{ y: -5 }}
            >
              {/* Rating */}
              <StarRating rating={testimonial.rating} />
              
              {/* Quote */}
              <blockquote className="text-gray-700 my-4 line-clamp-4">
                "{testimonial.quote}"
              </blockquote>

              {/* Información del cliente */}
              <div className="flex items-center gap-3 mt-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-semibold">
                  {testimonial.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <h5 className="font-bold text-gray-900">{testimonial.name}</h5>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                </div>
              </div>

              {/* Resultado principal */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <span className="text-sm font-semibold text-blue-600">
                  {testimonial.results[0]}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              ¿Listo para ser nuestro próximo caso de éxito?
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Únete a las empresas que ya están transformando sus operaciones con soluciones tecnológicas de vanguardia.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              Comenzar Proyecto
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Modal de Video */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={() => setActiveVideo(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl w-full bg-black rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActiveVideo(null)}
                className="absolute top-4 right-4 z-10 bg-white/20 text-white p-2 rounded-full hover:bg-white/30 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              <video
                controls
                autoPlay
                className="w-full h-auto"
                onPlay={() => setIsPlayingVideo(true)}
                onPause={() => setIsPlayingVideo(false)}
              >
                <source src={activeVideo.videoTestimonial} type="video/mp4" />
                Tu navegador no soporta el elemento de video.
              </video>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Testimonials;